import React, { useState, useEffect } from 'react';
import apiClient from '../../services/api';
import html2canvas from 'html2canvas';
import ReportIssueModal from '../StudentUniform/ReportIssueModal';
import './AuthorizationManagement.css';
import Swal from 'sweetalert2';

const AuthorizationManagement = () => {
  const [resources, setResources] = useState([]);
  const [roles] = useState(['TEACHER', 'HEADMASTER', 'PPD', 'ADMIN', 'SUPERADMIN', 'KTC']);
  const [selectedResourceId, setSelectedResourceId] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [originalPermissions, setOriginalPermissions] = useState({});
  const [showOldResources, setShowOldResources] = useState(false); // Hide old resources by default

  // Report Issue modal states
  const [showReportIssueModal, setShowReportIssueModal] = useState(false);
  const [screenshot, setScreenshot] = useState(null);

  // Define all possible permission types
  const allPermissionTypes = [
    { key: 'Access', label: 'Access', options: ['DENY', 'ALLOW', 'TRUE', 'FALSE'] },
    { key: 'DataScope', label: 'Data Scope', options: ['NONE', 'OWN_SCHOOL', 'OWN_DISTRICT', 'ALL'] },
    { key: 'ICAccess', label: 'IC Access', options: ['DENY', 'MASKED', 'FULL'] },
    { key: 'Submit', label: 'Submit', options: ['DENY', 'ALLOW', 'TRUE', 'FALSE'] },
    { key: 'ReopenSubmission', label: 'Reopen Submission', options: ['DENY', 'ALLOW', 'TRUE', 'FALSE'] },
    { key: 'ManageSchool', label: 'Manage School', options: ['DENY', 'ALLOW', 'TRUE', 'FALSE'] }
  ];

  // Function to determine which permission types are applicable for a resource
  const getApplicablePermissionTypes = (resource) => {
    if (!resource) return [];

    const resourceCode = resource.resourceCode.toLowerCase();
    const category = resource.category?.toLowerCase() || '';

    // Universal permission types (apply to ALL resources)
    const universal = ['Access', 'DataScope'];

    // Resource-specific permission types
    const resourceSpecific = {
      // Reports that need Submit and Reopen
      '/report-school': ['Submit', 'ReopenSubmission'],
      '/report-district': ['Submit', 'ReopenSubmission'],

      // Student management that needs Submit and IC Access
      '/student-uniform': ['Submit'],
      '/student-master': ['Submit', 'ICAccess'],

      // User management that might need ManageSchool
      '/user-management': ['ManageSchool'],

      // Dashboards and other pages typically only need Access and DataScope
      '/school-dashboard': [],
      '/ppd-dashboard': [],
      '/admin-dashboard': [],
      '/user-profile': [],
      '/user-audit-log': [],
      '/authorization-management': []
    };

    // Get applicable actions for this resource
    const specificActions = resourceSpecific[resourceCode] || [];
    const applicableActionKeys = [...universal, ...specificActions];

    // Filter and return only applicable permission types
    return allPermissionTypes.filter(pt => applicableActionKeys.includes(pt.key));
  };

  // Get permission types for currently selected resource
  const permissionTypes = selectedResource ? getApplicablePermissionTypes(selectedResource) : [];

  useEffect(() => {
    loadResources();
  }, []);

  useEffect(() => {
    if (selectedResourceId) {
      loadResourcePermissions();
    }
  }, [selectedResourceId]);

  const loadResources = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/AuthorizationManagement/resources');
      if (response.data.success) {
        setResources(response.data.resources);
      }
    } catch (error) {
      console.error('Error loading resources:', error);
      Swal.fire({
        icon: 'error',
        title: 'Load Failed',
        text: 'Failed to load resources',
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadResourcePermissions = async () => {
    try {
      const response = await apiClient.get(`/AuthorizationManagement/permissions/${selectedResourceId}`);
      if (response.data.success) {
        const permsData = {};
        response.data.permissions.forEach(p => {
          const key = `${p.role}_${p.actionCode}`;
          permsData[key] = p.permissionValue;
        });
        setPermissions(permsData);
        setOriginalPermissions(permsData);
      }
    } catch (error) {
      console.error('Error loading permissions:', error);
    }
  };

  const handleResourceSelect = (resource) => {
    setSelectedResourceId(resource.resourceId);
    setSelectedResource(resource);
    setSuccessMessage('');
  };

  const handlePermissionChange = (role, actionCode, value) => {
    const key = `${role}_${actionCode}`;
    setPermissions(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const getPermissionValue = (role, actionCode) => {
    const key = `${role}_${actionCode}`;
    return permissions[key] || (actionCode === 'DataScope' ? 'NONE' : 'DENY');
  };

  const handleCancel = () => {
    setPermissions(originalPermissions);
    setSuccessMessage('');
  };

  const handleSave = async () => {
    try {
      // Save permissions for each role
      for (const role of roles) {
        // Build permission object based on applicable permission types for this resource
        const permissionData = {
          role: role,
          resourceId: selectedResourceId,
          access: false,
          dataScope: 'NONE',
          icAccess: 'DENY',
          submit: false,
          reopenSubmission: false,
          manageSchool: false
        };

        // Set values based on applicable permission types
        permissionTypes.forEach(pt => {
          const value = getPermissionValue(role, pt.key);

          switch (pt.key) {
            case 'Access':
              permissionData.access = value === 'ALLOW' || value === 'TRUE';
              break;
            case 'DataScope':
              permissionData.dataScope = value;
              break;
            case 'ICAccess':
              permissionData.icAccess = value;
              break;
            case 'Submit':
              permissionData.submit = value === 'ALLOW' || value === 'TRUE';
              break;
            case 'ReopenSubmission':
              permissionData.reopenSubmission = value === 'ALLOW' || value === 'TRUE';
              break;
            case 'ManageSchool':
              permissionData.manageSchool = value === 'ALLOW' || value === 'TRUE';
              break;
          }
        });

        // Save this role's permissions
        await apiClient.post('/AuthorizationManagement/grant-role-access', permissionData);
      }

      setSuccessMessage('Permissions saved successfully!');
      setOriginalPermissions(permissions);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error saving permissions:', error);
      Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: 'Failed to save permissions: ' + (error.response?.data?.message || error.message),
        confirmButtonColor: '#dc3545'
      });
    }
  };

  const getResourceIcon = (category) => {
    const icons = {
      'Student Management': 'fas fa-user-graduate',
      'Dashboard': 'fas fa-chart-line',
      'System': 'fas fa-cog',
      'User Management': 'fas fa-users',
      'Administration': 'fas fa-shield-alt',
      'Reports': 'fas fa-file-alt'
    };
    return icons[category] || 'fas fa-file';
  };

  // Handle Report Issue button click
  const handleReportIssueClick = async () => {
    try {
      Swal.fire({
        title: 'Capturing Screenshot...',
        html: 'Please wait while we capture the screen...',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        scrollY: -window.scrollY,
        scrollX: -window.scrollX,
        width: window.innerWidth,
        height: window.innerHeight
      });

      const imageData = canvas.toDataURL('image/png');
      setScreenshot(imageData);
      setShowReportIssueModal(true);
      Swal.close();
    } catch (error) {
      console.error('Error capturing screenshot:', error);
      Swal.fire({
        icon: 'error',
        title: 'Capture Failed',
        text: 'Failed to capture screenshot. Please try again.'
      });
    }
  };

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  return (
    <div className="auth-management">
      {/* Header with Logo and Title */}
      <div className="header-container">
        <img className="logo" src="/KTC.png" alt="KTC" />
        <h2>Authorization Management</h2>
        <button className="header-btn btn-report-issue" onClick={handleReportIssueClick} title="Report Issue">
          <i className="fas fa-bug"></i>
        </button>
      </div>
      <div className="page-header" style={{ display: 'none' }}>
        <div className="header-content" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div>
            <h1><i className="fas fa-shield-alt"></i> Authorization Management</h1>
            <p>Manage role-based permissions and access control for all system resources</p>
          </div>
          <button
            className="btn btn-report-issue"
            onClick={handleReportIssueClick}
            title="Report Issue"
            style={{
              padding: '8px 16px',
              background: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <i className="fas fa-bug"></i>
            Report Issue
          </button>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="alert-success-container">
          <div className="alert-success">
            <i className="fas fa-check-circle"></i> {successMessage}
          </div>
        </div>
      )}

      {/* Main Container - Sidebar Layout */}
      <div className="auth-main-container">
        {/* Left Sidebar - Resource List */}
        <div className="auth-sidebar">
          <div className="auth-sidebar-header">
            <h3><i className="fas fa-list"></i> Resources</h3>
            <button
              className="btn-toggle-old"
              onClick={() => setShowOldResources(!showOldResources)}
              title={showOldResources ? "Hide old application resources" : "Show old application resources"}
            >
              <i className={`fas fa-${showOldResources ? 'eye-slash' : 'eye'}`}></i>
              {showOldResources ? ' Hide Old' : ' Show Old'}
            </button>
          </div>

          {/* OLD Application Resources (ResourceCode doesn't start with /) */}
          {showOldResources && resources.filter(r => !r.resourceCode.startsWith('/')).length > 0 && (
            <>
              <div className="resource-group-header old-app">
                <i className="fas fa-archive"></i> OLD APPLICATION
              </div>
              <ul className="auth-resource-list">
                {resources
                  .filter(r => !r.resourceCode.startsWith('/'))
                  .sort((a, b) => a.resourceName.localeCompare(b.resourceName))
                  .map(resource => (
                    <li
                      key={resource.resourceId}
                      className={`auth-resource-item ${selectedResourceId === resource.resourceId ? 'auth-active' : ''}`}
                      onClick={() => handleResourceSelect(resource)}
                    >
                      <span className="auth-resource-icon">
                        <i className={getResourceIcon(resource.category)}></i>
                      </span>
                      <div className="auth-resource-info">
                        <div className="auth-resource-name">
                          {resource.resourceName}
                          <span className="resource-badge old">OLD</span>
                        </div>
                        <div className="auth-resource-code">{resource.resourceCode}</div>
                      </div>
                    </li>
                  ))}
              </ul>
            </>
          )}

          {/* NEW Application Resources (ResourceCode starts with /) */}
          {resources.filter(r => r.resourceCode.startsWith('/')).length > 0 && (
            <>
              <div className="resource-group-header new-app">
                <i className="fas fa-star"></i> NEW APPLICATION
              </div>
              <ul className="auth-resource-list">
                {resources
                  .filter(r => r.resourceCode.startsWith('/'))
                  .sort((a, b) => a.resourceName.localeCompare(b.resourceName))
                  .map(resource => (
                    <li
                      key={resource.resourceId}
                      className={`auth-resource-item ${selectedResourceId === resource.resourceId ? 'auth-active' : ''}`}
                      onClick={() => handleResourceSelect(resource)}
                    >
                      <span className="auth-resource-icon">
                        <i className={getResourceIcon(resource.category)}></i>
                      </span>
                      <div className="auth-resource-info">
                        <div className="auth-resource-name">
                          {resource.resourceName}
                          <span className="resource-badge new">NEW</span>
                        </div>
                        <div className="auth-resource-code">{resource.resourceCode}</div>
                      </div>
                    </li>
                  ))}
              </ul>
            </>
          )}
        </div>

        {/* Right Panel - Permissions Grid */}
        <div className="auth-content-panel">
          <div className="alert-info">
            <i className="fas fa-info-circle"></i>
            <strong>Database-Driven:</strong> All permission changes take effect immediately. No code deployment required.
          </div>

          {!selectedResource ? (
            <div className="no-selection">
              <i className="fas fa-hand-pointer"></i>
              <h3>Select a Resource</h3>
              <p>Click on a resource from the left panel to view and manage permissions</p>
            </div>
          ) : (
            <>
              {/* Resource Header */}
              <div className="resource-header">
                <h2>
                  <i className={getResourceIcon(selectedResource.category)}></i>
                  {selectedResource.resourceName}
                </h2>
                <span className="resource-type-badge">
                  <i className="fas fa-tag"></i> {selectedResource.category}
                </span>
                <div className="resource-description">
                  {selectedResource.description}
                </div>
              </div>

              {/* Permissions Grid */}
              <div className="permissions-card">
                <h3><i className="fas fa-key"></i> Role Permissions</h3>

                <table className="permission-table">
                  <thead>
                    <tr>
                      <th>PERMISSION TYPE</th>
                      {roles.map(role => (
                        <th key={role}>{role}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {permissionTypes.map(pt => (
                      <tr key={pt.key}>
                        <td>{pt.label}</td>
                        {roles.map(role => (
                          <td key={role}>
                            <select
                              className="permission-dropdown"
                              value={getPermissionValue(role, pt.key)}
                              onChange={(e) => handlePermissionChange(role, pt.key, e.target.value)}
                            >
                              {pt.options.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="button-group">
                  <button className="btn-secondary-custom" onClick={handleCancel}>
                    Cancel
                  </button>
                  <button className="btn-success-custom" onClick={handleSave}>
                    Save Changes
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Report Issue Modal */}
      {showReportIssueModal && (
        <ReportIssueModal
          show={showReportIssueModal}
          onClose={() => setShowReportIssueModal(false)}
          screenshot={screenshot}
          context={{
            screen: 'Authorization Management',
            selectedResource: selectedResource ? {
              id: selectedResource.resourceId,
              name: selectedResource.resourceName,
              code: selectedResource.resourceCode,
              category: selectedResource.category
            } : null,
            permissions: permissions,
            roles: roles
          }}
        />
      )}
    </div>
  );
};

export default AuthorizationManagement;
